// File Metadata:
// Version: 4.00
// Created: 2025-04-07
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: "Obsidian Home" design system — teal/amber palette, Bricolage Grotesque + Epilogue fonts
// Last Modified: 2026-03-01
// Filename: theme.js

import { createTheme, alpha } from '@mui/material/styles';

const FONT_DISPLAY = '"Bricolage Grotesque", "Helvetica Neue", sans-serif';
const FONT_BODY    = '"Epilogue", "Helvetica Neue", sans-serif';

// ── Palette tokens ──
const dark = {
  bg:          '#0e1117',
  bgPaper:     '#161c27',
  bgAlt:       '#1e2736',
  border:      'rgba(255,255,255,0.07)',
  divider:     'rgba(255,255,255,0.07)',
  primary:     '#2dd4bf',
  primaryDark: '#0d9488',
  secondary:   '#fbbf24',
  textPrimary:   '#e2e8f0',
  textSecondary: '#94a3b8',
  textDisabled:  '#64748b',  // improved from #475569 for WCAG 4.5:1 contrast on dark bg
};

const light = {
  bg:          '#f0f4f8',
  bgPaper:     '#ffffff',
  bgAlt:       '#e8edf5',
  border:      'rgba(0,0,0,0.06)',
  divider:     'rgba(0,0,0,0.08)',
  primary:     '#0d9488',
  primaryDark: '#0f766e',
  secondary:   '#d97706',
  textPrimary:   '#0f172a',
  textSecondary: '#475569',
  textDisabled:  '#78909c',  // improved from #94a3b8 for WCAG 4.5:1 contrast on light bg
};

const getDesignTokens = (mode) => {
  const c = mode === 'dark' ? dark : light;
  const isDark = mode === 'dark';

  return {
    palette: {
      mode,
      primary: {
        main:         c.primary,
        light:        isDark ? '#5eead4' : '#2dd4bf',
        dark:         c.primaryDark,
        contrastText: isDark ? '#0f172a' : '#ffffff',
      },
      secondary: {
        main:         c.secondary,
        light:        isDark ? '#fde68a' : '#fcd34d',
        dark:         isDark ? '#d97706' : '#b45309',
        contrastText: '#0f172a',
      },
      error: {
        main:  isDark ? '#f43f5e' : '#e11d48',
        light: isDark ? '#fb7185' : '#fb7185',
        dark:  isDark ? '#e11d48' : '#be123c',
      },
      warning: {
        main:  c.secondary,
        light: isDark ? '#fde68a' : '#fcd34d',
        dark:  isDark ? '#d97706' : '#b45309',
      },
      success: {
        main:  isDark ? '#34d399' : '#059669',
        light: isDark ? '#6ee7b7' : '#34d399',
        dark:  isDark ? '#10b981' : '#047857',
      },
      info: {
        main:  isDark ? '#38bdf8' : '#0284c7',
        light: isDark ? '#7dd3fc' : '#38bdf8',
        dark:  isDark ? '#0284c7' : '#0369a1',
      },
      background: {
        default: c.bg,
        paper:   c.bgPaper,
        alt:     c.bgAlt,
      },
      text: {
        primary:   c.textPrimary,
        secondary: c.textSecondary,
        disabled:  c.textDisabled,
      },
      divider: c.divider,
      action: {
        hover:    alpha(c.primary, 0.08),
        selected: alpha(c.primary, 0.14),
        disabled: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
        focus:    alpha(c.primary, 0.18),
      },
    },

    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1104, xl: 1536 },
    },

    typography: {
      fontFamily: FONT_BODY,
      h1: { fontFamily: FONT_DISPLAY, fontSize: '2.75rem', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15 },
      h2: { fontFamily: FONT_DISPLAY, fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
      h3: { fontFamily: FONT_DISPLAY, fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3 },
      h4: { fontFamily: FONT_DISPLAY, fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.35 },
      h5: { fontFamily: FONT_DISPLAY, fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
      h6: { fontFamily: FONT_DISPLAY, fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.5 },
      body1: { fontSize: '1rem', lineHeight: 1.65, letterSpacing: '0em' },
      body2: { fontSize: '0.875rem', lineHeight: 1.6, letterSpacing: '0.005em' },
      button: { fontFamily: FONT_BODY, fontSize: '0.9rem', fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' },
      caption: { fontSize: '0.75rem', lineHeight: 1.4, letterSpacing: '0.02em' },
      overline: { fontFamily: FONT_DISPLAY, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: '0.005em' },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: '0.005em' },
    },

    shape: { borderRadius: 12 },

    // Chat-specific design tokens (preserved for backwards compat)
    chat: {
      groupWindowMs: 5 * 60 * 1000,
      avatarSize: 36,
      bubble: { radii: { default: 12, connectedSmall: 4 }, maxWidth: 560 },
    },

    shadows: Array.from({ length: 25 }, (_, i) => {
      if (i === 0) return 'none';
      const depth = Math.min(i / 24, 1);
      return isDark
        ? `0 ${i}px ${i * 3}px rgba(0,0,0,${0.2 + depth * 0.3})`
        : `0 ${i}px ${i * 3}px rgba(0,0,0,${0.04 + depth * 0.12})`;
    }),

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: c.bg,
            colorScheme: isDark ? 'dark' : 'light',
          },
          body: {
            fontFamily: FONT_BODY,
            backgroundColor: c.bg,
            color: c.textPrimary,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '*': { boxSizing: 'border-box' },
          '*::before, *::after': { boxSizing: 'border-box' },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            fontFamily: FONT_BODY,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 10,
            padding: '9px 20px',
            fontSize: '0.9rem',
            transition: 'all 200ms cubic-bezier(0.4,0,0.2,1)',
          },
          contained: {
            '&&': {
              backgroundColor: c.primary,
              backgroundImage: `linear-gradient(135deg, ${c.primary} 0%, ${c.primaryDark} 100%)`,
              color: isDark ? '#0f172a' : '#ffffff',
              boxShadow: `0 2px 12px ${alpha(c.primary, 0.28)}`,
            },
            '&&:active': { transform: 'translateY(0)' },
            '&&:hover': {
              backgroundColor: c.primaryDark,
              backgroundImage: `linear-gradient(135deg, ${c.primaryDark} 0%, ${alpha(c.primaryDark, 0.85)} 100%)`,
              boxShadow: `0 4px 20px ${alpha(c.primary, 0.42)}`,
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            '&&': {
              borderWidth: '1.5px',
              borderColor: alpha(c.primary, 0.5),
              color: c.primary,
            },
            '&&:hover': {
              borderWidth: '1.5px',
              borderColor: c.primary,
              backgroundColor: alpha(c.primary, 0.07),
              transform: 'translateY(-1px)',
            },
            '&&:active': { transform: 'translateY(0)' },
          },
          text: {
            '&&': {
              color: c.primary,
            },
            '&&:hover': { backgroundColor: alpha(c.primary, 0.07) },
          },
          sizeLarge: { padding: '11px 28px', fontSize: '1rem', borderRadius: 12 },
          sizeSmall: { padding: '5px 14px', fontSize: '0.8rem', borderRadius: 8 },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: c.bgPaper,
            border: `1px solid ${c.border}`,
            boxShadow: isDark
              ? '0 4px 24px rgba(0,0,0,0.32), 0 1px 4px rgba(0,0,0,0.2)'
              : '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
            transition: 'transform 200ms ease, box-shadow 200ms ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: isDark
                ? '0 12px 40px rgba(0,0,0,0.48), 0 2px 8px rgba(0,0,0,0.24)'
                : '0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06)',
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: c.bgPaper,
            backgroundImage: 'none',
            border: `1px solid ${c.border}`,
            boxShadow: 'none',
          },
          elevation0: { boxShadow: 'none', border: `1px solid ${c.border}` },
          elevation1: {
            boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.06)',
          },
          elevation4: {
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.08)',
          },
          elevation8: {
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.1)',
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: isDark ? alpha('#1e2736', 0.8) : alpha('#f8fafc', 0.8),
              fontFamily: FONT_BODY,
              transition: 'all 200ms ease',
              '& fieldset': { borderColor: c.border, transition: 'border-color 200ms ease' },
              '&:hover fieldset': { borderColor: alpha(c.primary, 0.5) },
              '&.Mui-focused fieldset': { borderColor: c.primary, borderWidth: '1.5px' },
              '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(c.primary, 0.12)}` },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: c.primary },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: { fontFamily: FONT_BODY, fontSize: '0.9rem' },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 500, fontFamily: FONT_BODY },
          filled: {
            backgroundColor: alpha(c.primary, isDark ? 0.16 : 0.1),
            color: c.primary,
            '&:hover': { backgroundColor: alpha(c.primary, isDark ? 0.25 : 0.18) },
          },
          outlined: { borderColor: alpha(c.primary, 0.4), color: c.primary },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? alpha(c.bg, 0.85) : alpha(c.bgPaper, 0.90),
            backgroundImage: 'none',
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            color: c.textPrimary,
            boxShadow: 'none',
            borderBottom: `1px solid ${c.border}`,
            borderRadius: 0,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? alpha(c.bg, 0.92) : c.bgPaper,
            backgroundImage: 'none',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRight: `1px solid ${c.border}`,
            boxShadow: 'none',
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            padding: '8px 12px',
            position: 'relative',
            transition: 'all 150ms ease',
            '&:hover': { backgroundColor: alpha(c.primary, 0.08) },
            '&.Mui-selected': {
              backgroundColor: alpha(c.primary, 0.14),
              color: c.primary,
              '& .MuiListItemIcon-root': { color: c.primary },
              '&:hover': { backgroundColor: alpha(c.primary, 0.2) },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '20%',
                height: '60%',
                width: '3px',
                borderRadius: '0 3px 3px 0',
                backgroundColor: c.primary,
              },
            },
          },
        },
      },

      MuiListItemIcon: {
        styleOverrides: {
          root: { color: c.textSecondary, minWidth: 38, transition: 'color 150ms ease' },
        },
      },

      MuiListItemText: {
        styleOverrides: {
          primary: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: '0.9rem' },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: c.divider } },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: 'all 150ms ease',
            '&:hover': {
              backgroundColor: alpha(c.primary, 0.1),
              transform: 'scale(1.05)',
            },
          },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${c.border}`,
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)'
              : '0 8px 24px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.06)',
            backgroundColor: c.bgPaper,
            backdropFilter: 'blur(12px)',
            backgroundImage: 'none',
            padding: '4px',
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 0',
            padding: '8px 12px',
            fontSize: '0.875rem',
            fontFamily: FONT_BODY,
            '&:hover': { backgroundColor: alpha(c.primary, 0.08) },
            '&.Mui-selected': {
              backgroundColor: alpha(c.primary, 0.12),
              color: c.primary,
              '&:hover': { backgroundColor: alpha(c.primary, 0.18) },
            },
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 26,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: 2,
              transitionDuration: '300ms',
              '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  backgroundColor: c.primary,
                  opacity: 1,
                  border: 0,
                },
              },
            },
            '& .MuiSwitch-thumb': { boxSizing: 'border-box', width: 22, height: 22 },
            '& .MuiSwitch-track': {
              borderRadius: 13,
              backgroundColor: isDark ? '#334155' : '#cbd5e1',
              opacity: 1,
              transition: 'background-color 300ms',
            },
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontFamily: FONT_BODY,
            fontSize: '0.875rem',
            borderRadius: 8,
            margin: '0 2px',
            minHeight: 40,
            '&:hover': { backgroundColor: alpha(c.primary, 0.08) },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: c.primary,
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 18,
            border: `1px solid ${c.border}`,
            boxShadow: isDark ? '0 24px 60px rgba(0,0,0,0.6)' : '0 24px 60px rgba(0,0,0,0.15)',
            backgroundImage: 'none',
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, fontFamily: FONT_BODY },
        },
      },

      MuiTableContainer: {
        styleOverrides: {
          root: { borderRadius: 14, border: `1px solid ${c.border}` },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: { backgroundColor: isDark ? c.bgAlt : alpha(c.bg, 0.5) },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            fontFamily: FONT_DISPLAY,
            fontSize: '0.8rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: c.textSecondary,
          },
          root: { borderBottom: `1px solid ${c.divider}`, fontFamily: FONT_BODY },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            backgroundColor: isDark ? '#334155' : '#1e293b',
            color: '#f8fafc',
            fontSize: '0.75rem',
            fontFamily: FONT_BODY,
          },
          arrow: { color: isDark ? '#334155' : '#1e293b' },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4, height: 6 },
          bar: {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${c.primary}, ${isDark ? '#5eead4' : '#2dd4bf'})`,
          },
        },
      },

      MuiBadge: {
        styleOverrides: {
          badge: { fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.65rem' },
        },
      },
    },
  };
};

export const createAppTheme = (mode = 'dark') => createTheme(getDesignTokens(mode));

// Default export as dark theme
const theme = createAppTheme('dark');
export default theme;